/**
 * Promo de temporada (Cyber Monday, Hot Sale…): el banner con el logo del evento y la
 * cuenta regresiva que acompaña al descuento del primer mes en las pantallas de pago
 * (ADR-2026-11-02-sello-cyber-monday-en-pantallas-de-pago, ADR-2026-11-02-banner-cyber-monday-como-la-landing).
 *
 * Es SÓLO la marca: el descuento lo pone el cupón invisible (`lib/hotSaleCoupon`) y
 * quién lo recibe lo decide `cambioDePlanAdmiteDescuento`. Por eso el banner se muestra
 * únicamente donde ya hay un precio con descuento, y el % sale del cupón, no de acá.
 *
 * Los eventos son de Argentina (`cybermonday.com.ar`, `hotsale.com.ar`): a una cuenta
 * de otro país el logo no le dice nada, aunque reciba el mismo descuento por Stripe.
 *
 * No se apaga sola: el banner sigue mientras haya descuento, y el reloj, pasado
 * `cuentaHasta`, cuenta hasta el final de cada día
 * (ADR-2026-11-02-banner-cyber-monday-como-la-landing-6). Para sacarla, `null`; para la
 * próxima promo, cambiar el objeto.
 */
export interface PromoDeTemporada {
  /** Para el `alt` del logo. */
  nombre: string;
  /** Logo con fondo transparente, en `public/`. */
  logo: string;
  /** Fondo del banner: los colores del EVENTO, no los de la marca (no siguen al branding). */
  fondo: string;
  /** Color de las cifras de la cuenta regresiva, sobre las cajitas blancas. */
  colorDeCifras: string;
  /** Hasta dónde cuenta el reloj. Pasado eso, cuenta hasta el final de cada día. */
  cuentaHasta: Date;
}

/** La promo con el % del cupón que la acompaña, ya listo para mostrar. */
export interface PromoActiva {
  evento: PromoDeTemporada;
  /** Entero, ej. `40`. */
  descuento: number;
}

export const PROMO_DE_TEMPORADA: PromoDeTemporada | null = {
  nombre: 'Cyber Monday',
  logo: '/images/promos/cyber-monday.webp',
  // El degradé del logo (celeste → azul → violeta) con un velo más oscuro al centro para
  // que el blanco se lea. Es el mismo del banner de la landing (`CyberMonday.tsx`).
  fondo:
    'radial-gradient(45% 75% at 50% 50%, rgba(20,40,170,0.55) 0%, transparent 100%), linear-gradient(115deg, #2fb2f8 0%, #2f7ff0 22%, #3557e6 48%, #5e50d7 74%, #7b34d8 100%)',
  colorDeCifras: '#3557e6',
  // El mismo cierre que la landing: el final del viernes 6/11.
  cuentaHasta: new Date('2026-11-07T00:00:00-03:00'),
};

/** La promo a mostrar, o `null` si no hay o la cuenta no es argentina. */
export function promoVigente(
  esArgentina: boolean,
  promo: PromoDeTemporada | null = PROMO_DE_TEMPORADA,
): PromoDeTemporada | null {
  return promo && esArgentina ? promo : null;
}

/** `multiplicador` del cupón (factor de precio, 0.6 = paga 60%) → % entero de descuento. */
export function porcentajeDeDescuento(multiplicador: number): number {
  return Math.round((1 - multiplicador) * 100);
}

const HORA = 3_600_000;
// Argentina es UTC−3 todo el año (sin horario de verano): el reloj no depende del
// navegador de quien mira.
const AR = -3 * HORA;

/** Las 00:00 de mañana en Argentina. */
function finDelDiaEnArgentina(ahora: number): number {
  const ar = new Date(ahora + AR);
  return Date.UTC(ar.getUTCFullYear(), ar.getUTCMonth(), ar.getUTCDate() + 1) - AR;
}

/**
 * Cuánto falta. Cuenta hasta `cuentaHasta`; si ya pasó, hasta el final del día de hoy,
 * para que nunca quede en cero mientras la promo sigue (igual que la landing).
 */
export function faltaParaElCierre(
  ahora: number,
  cuentaHasta: Date,
): { dias: number; horas: number; minutos: number; segundos: number } {
  const fin = cuentaHasta.getTime();
  const objetivo = ahora < fin ? fin : finDelDiaEnArgentina(ahora);
  const s = Math.max(0, Math.floor((objetivo - ahora) / 1000));
  return {
    dias: Math.floor(s / 86400),
    horas: Math.floor(s / 3600) % 24,
    minutos: Math.floor(s / 60) % 60,
    segundos: s % 60,
  };
}
